const ERROR_DIV_ZERO = "DIV/0";

const ERROR = "ERROR";

const QUOTIENT = "QUOTIENT";

const NAME_ERROR = "#NAME?";

/*
  Function to parse a formula with given variable values

  Example usage:

  import { Parser } from "hot-formula-parser";
  import { parseFormula } from "./helpers";

  const formula = 'variables.a + variables.b';
  const variableValues = { a: 5, b: 10 };
  const { result, error } = parseFormula(Parser, formula, variableValues);
*/
export const parseFormula = (Parser, formula, variableValues) => {
  let parser = new Parser();

  Object.keys(variableValues).forEach((k) => {
    parser.setVariable(k, variableValues[k]);
  });

  // Override QUOTIENT to return DIV_ZERO ERROR
  parser.on("callFunction", (name, params, done) => {
    if (name.toUpperCase() == QUOTIENT) {
      if (params[1] === void 0) {
        throw Error(ERROR);
      } else if (params[1] == 0) {
        throw Error(ERROR_DIV_ZERO);
      }

      done(parseInt(params[0] / params[1], 10));
    }
  });

  const aliasAverage = (params) => parser.parse(`AVERAGE(${params.join(",")})`).result;

  const selectionCount = params => parser.parse(`COUNT(${params.join(",")})`).result;

  // EXTRACT_ROWS only works with the form repeatable region style rows: {row-1: ..., row-2: ..., ...}
  const extractRows = (params) => {
    return Object.entries(params[0])
      .filter(([key]) => key.startsWith("row-")) // ignores "row_count" (added after submission)
      .map(([, row]) => row[params[1]]); 
  }

  parser.setFunction("avg", aliasAverage);
  parser.setFunction("AVG", aliasAverage);
  parser.setFunction("EXTRACT_ROWS", extractRows); // Extract rows from repeatable region
  parser.setFunction("SELECTION_COUNT", selectionCount);
  parser.setFunction("selection_count", selectionCount);

  return parser.parse(formula);
};

export const isValidFormula = (Parser, formula, variables) => {
  const { error } = parseFormula(Parser, formula, Object.fromEntries(variables.map((v) => [v, 1])));
  return error != NAME_ERROR;
};

export const getDependencies = (Parser, formula, variables) => {
  let dependencies = [];

  // Formula has to be valid with the variable
  if (formula && isValidFormula(Parser, formula, variables)) {
    // Formula is dependent on another variable if it's not valid when variable value is omitted but other variables are present.
    dependencies = variables.filter(
      (v) =>
        !isValidFormula(
          Parser,
          formula,
          variables.filter((va) => v != va)
        )
    );
  }

  return dependencies;
};

export const sortFormulasBasedOnDependencies = (Parser, formulas) => {
  const arr = formulas.map((f) => ({
    ...f,
    dependencies: getDependencies(
      Parser,
      f.formula,
      formulas.filter((fo) => fo.key != f.key).map((fo) => fo.key)
    ),
  }));

  // If formula doesn't have any dependencies, add to result
  // Otherwise add to dependent
  let result = arr.filter((f) => f.dependencies.length == 0);
  let dependent = arr.filter((f) => f.dependencies.length > 0);
  let prevResults = 0;

  while (prevResults != result.length) {
    // If any results were added, iterate one more time.
    prevResults = result.length;

    dependent = dependent.filter((f) => {
      if (f.dependencies.every((d) => result.find((r) => r.key == d))) {
        result.push(f);
        return false;
      }

      return true;
    });
  }

  return result.concat(dependent);
};
